import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Tabs,
  Tab
} from '@mui/material';
import {
  GitHub as GitHubIcon,
  Launch as LaunchIcon,
  ViewKanban as ViewKanbanIcon,
  Timeline as TimelineIcon,
  BarChart as StatsIcon,
  AutoAwesome as AIIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon
} from '@mui/icons-material';
import { useAppContext } from './context/AppContext';
import ApplicationDialog from './components/ApplicationDialog';
import ProductDialog from './components/ProductDialog';
import ReleaseDialog from './components/ReleaseDialog';
import ReleaseDetailsDialog from './components/ReleaseDetailsDialog';
import RoadmapView from './components/RoadmapView';
import ReleaseTimeline from './components/ReleaseTimeline';
import ReleaseStatistics from './components/ReleaseStatistics';
import MarketingSummaryDialog from './components/MarketingSummaryDialog';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import { Release, ReleaseStatus } from './types';
import { fetchGitHubReleases, categorizeReleases, generatePlannedReleases } from './utils/githubApi';
import { summarizeReleaseForMarketing, MarketingSummary } from './utils/azureOpenAI';

function App() {
  const {
    products,
    applications,
    selectedProductId,
    selectedAppId,
    themeMode,
    toggleTheme,
    addProduct,
    selectProduct,
    selectApplication,
    addApplication,
    updateApplication,
    addRelease,
    updateRelease,
    deleteRelease
  } = useAppContext();

  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [releaseDetailsOpen, setReleaseDetailsOpen] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [editingRelease, setEditingRelease] = useState<Release | null>(null);
  const [newReleaseStatus, setNewReleaseStatus] = useState<ReleaseStatus>('Planned');
  const [currentView, setCurrentView] = useState<number>(0);
  const [isFetchingReleases, setIsFetchingReleases] = useState(false);
  const [marketingSummaryOpen, setMarketingSummaryOpen] = useState(false);
  const [marketingSummary, setMarketingSummary] = useState<MarketingSummary | null>(null);
  const [isSummarizing, setIsSummarizing] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [selectedReleaseForSummary, setSelectedReleaseForSummary] = useState<Release | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Filter applications by selected product
  const filteredApplications = selectedProductId
    ? applications.filter(app => app.productId === selectedProductId)
    : applications;

  const selectedApp = filteredApplications.find(app => app.id === selectedAppId);

  // Initial loading effect
  useEffect(() => {
    // Simulate initial load time (minimum 2 seconds to show the loading screen)
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Auto-fetch releases when application is selected
  useEffect(() => {
    const autoFetchReleases = async () => {
      if (!selectedApp || !selectedApp.githubRepoUrl) return;
      
      // Skip if releases already exist
      if (selectedApp.releases && selectedApp.releases.length > 0) return;
      
      setIsFetchingReleases(true);
      try {
        const releases = await fetchGitHubReleases(selectedApp.githubRepoUrl);
        const categorized = categorizeReleases(releases);
        
        // Generate planned releases from GitHub Projects if URL is available
        let allReleases = [...categorized];
        
        if (selectedApp.githubProjectsUrl && categorized.length > 0) {
          try {
            // Find the current production release version and date
            const currentRelease = categorized.find(r => r.status === 'Current' && r.releaseType === 'Production');
            const currentVersion = currentRelease?.version || categorized[0]?.version || '1.0.0';
            const currentReleaseDate = currentRelease?.releaseDate || categorized[0]?.releaseDate;
            
            // Generate planned releases based on work items
            const plannedReleases = await generatePlannedReleases(
              selectedApp.githubProjectsUrl,
              currentVersion,
              selectedApp.name,
              currentReleaseDate
            );
            
            allReleases = [...categorized, ...plannedReleases];
            console.log(`Generated ${plannedReleases.length} planned releases`);
          } catch (error) {
            console.error('Failed to generate planned releases:', error);
            // Continue with just the fetched releases
          }
        }
        
        // Update the application with fetched and planned releases
        if (allReleases.length > 0) {
          updateApplication(selectedApp.id, { releases: allReleases });
        }
      } catch (error) {
        console.error('Failed to auto-fetch releases:', error);
      } finally {
        setIsFetchingReleases(false);
      }
    };

    autoFetchReleases();
  }, [selectedAppId, selectedApp?.githubRepoUrl, selectedApp?.githubProjectsUrl, selectedApp, updateApplication]);

  const handleAddProduct = (data: any) => {
    addProduct(data);
  };

  const handleAddApplication = (data: any) => {
    addApplication(data);
  };

  const handleAddRelease = (status: ReleaseStatus) => {
    setNewReleaseStatus(status);
    setEditingRelease(null);
    setReleaseDialogOpen(true);
  };

  const handleSaveRelease = (data: any) => {
    if (selectedAppId) {
      if (editingRelease) {
        updateRelease(selectedAppId, editingRelease.id, data);
      } else {
        addRelease(selectedAppId, { ...data, status: newReleaseStatus });
      }
    }
    setEditingRelease(null);
  };

  const handleReleaseClick = (release: Release) => {
    setSelectedRelease(release);
    setReleaseDetailsOpen(true);
  };

  const handleEditRelease = () => {
    if (selectedRelease) {
      setEditingRelease(selectedRelease);
      setReleaseDetailsOpen(false);
      setReleaseDialogOpen(true);
    }
  };

  const handleDeleteRelease = () => {
    if (selectedRelease && selectedAppId) {
      deleteRelease(selectedAppId, selectedRelease.id);
      setReleaseDetailsOpen(false);
      setSelectedRelease(null);
    }
  };



  const handleSummarizeRelease = async (releaseId?: string) => {
    if (!selectedApp || !selectedApp.releases || selectedApp.releases.length === 0) {
      setSummaryError('No releases available to summarize');
      setMarketingSummaryOpen(true);
      return;
    }

    // If no releaseId provided, just open the dialog
    if (!releaseId) {
      setMarketingSummaryOpen(true);
      setMarketingSummary(null);
      setSummaryError(null);
      return;
    }

    // Find the release by ID
    const releaseToSummarize = selectedApp.releases.find(r => r.id === releaseId);
    if (!releaseToSummarize) {
      setSummaryError('Could not find the release');
      return;
    }

    setSelectedReleaseForSummary(releaseToSummarize);
    setIsSummarizing(true);
    setSummaryError(null);
    setMarketingSummary(null);

    try {
      const summary = await summarizeReleaseForMarketing(
        releaseToSummarize.version,
        releaseToSummarize.description,
        selectedApp.name
      );
      setMarketingSummary(summary);
    } catch (error: any) {
      setSummaryError(error.message || 'Failed to generate marketing summary');
    } finally {
      setIsSummarizing(false);
    }
  };

  const handleVersionChangeInDialog = (releaseId: string) => {
    handleSummarizeRelease(releaseId);
  };

  // Show loading screen on initial load
  if (isInitialLoading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="static"
        sx={{
          background: (theme) => 
            `linear-gradient(to bottom, ${theme.palette.primary.main}, transparent)`,
          boxShadow: 'none'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 64, md: 64 } }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <img 
                src="https://raaghustorageaccount.blob.core.windows.net/pundit-blob/wai-logo.svg" 
                alt="AI Pundit Logo" 
                style={{ height: '1.5em', verticalAlign: 'middle', marginRight: '4px' }}
              />
              <strong>Wai Products Roadmap</strong>
            </span>
          </Typography>
          <IconButton color="inherit" onClick={toggleTheme} sx={{ mr: 1 }}>
            {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          {/* Hidden: Add Product/Application buttons
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => setProductDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            New Product
          </Button>
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={() => setAppDialogOpen(true)}
            disabled={products.length === 0}
          >
            New Application
          </Button>
          */}
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: { xs: 4, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 }, flexGrow: 1 }}>
        {products.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" gutterBottom color="text.secondary">
              No Data Yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Create your first product to organize applications
            </Typography>
            {/* Hidden: Create Product button
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setProductDialogOpen(true)}
            >
              Create Product
            </Button>
            */}
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}>
              <FormControl sx={{ minWidth: { xs: '100%', md: 250 } }}>
                <InputLabel>Select Product</InputLabel>
                <Select
                  value={selectedProductId || ''}
                  label="Select Product"
                  onChange={(e) => selectProduct(e.target.value)}
                >
                  {products.map((prod) => (
                    <MenuItem key={prod.id} value={prod.id}>
                      {prod.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: { xs: '100%', md: 250 } }} disabled={!selectedProductId}>
                <InputLabel>Select Application</InputLabel>
                <Select
                  value={selectedAppId || ''}
                  label="Select Application"
                  onChange={(e) => selectApplication(e.target.value)}
                >
                  {filteredApplications.map((app) => (
                    <MenuItem key={app.id} value={app.id}>
                      {app.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedApp && (
                <>
                  <Box sx={{ display: 'flex', gap: 1, flexGrow: 1, flexWrap: 'wrap' }}>
                    {selectedApp.githubRepoUrl && (
                      <Chip
                        icon={<GitHubIcon />}
                        label="Releases"
                        component="a"
                        href={`${selectedApp.githubRepoUrl}`}
                        target="_blank"
                        clickable
                      />
                    )}
                    {selectedApp.githubProjectsUrl && (
                      <Chip
                        icon={<GitHubIcon />}
                        label="Projects"
                        component="a"
                        href={selectedApp.githubProjectsUrl}
                        target="_blank"
                        clickable
                      />
                    )}
                    {selectedApp.applicationUrls.map((urlItem, idx) => (
                      <Chip
                        key={idx}
                        icon={<LaunchIcon />}
                        label={urlItem.name || `Website ${idx + 1}`}
                        component="a"
                        href={urlItem.url}
                        target="_blank"
                        clickable
                      />
                    ))}
                  </Box>
                  {/* Hidden: Delete menu options
                  <IconButton onClick={handleMenuOpen}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={handleMenuClose}
                  >
                    <MenuItemType onClick={handleDeleteApp}>
                      Delete Application
                    </MenuItemType>
                    {selectedProductId && (
                      <MenuItemType onClick={handleDeleteProduct}>
                        Delete Product ({selectedProduct?.name})
                      </MenuItemType>
                    )}
                  </Menu>
                  */}
                </>
              )}
            </Box>

            {selectedApp ? (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AIIcon />}
                    onClick={() => handleSummarizeRelease()}
                    disabled={!selectedApp?.releases || selectedApp.releases.length === 0}
                    size="small"
                  >
                    Summarize Release Notes
                  </Button>
                </Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs 
                    value={currentView} 
                    onChange={(_, newValue) => setCurrentView(newValue)}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab icon={<StatsIcon />} label="Statistics" iconPosition="start" />
                    <Tab icon={<TimelineIcon />} label="Timeline" iconPosition="start" />
                    <Tab icon={<ViewKanbanIcon />} label="Releases" iconPosition="start" />
                  </Tabs>
                </Box>

                {currentView === 0 && (
                  <ReleaseStatistics
                    releases={selectedApp.releases}
                    isLoading={isFetchingReleases}
                  />
                )}

                {currentView === 1 && (
                  <ReleaseTimeline
                    releases={selectedApp.releases}
                    onReleaseClick={handleReleaseClick}
                    isLoading={isFetchingReleases}
                  />
                )}

                {currentView === 2 && (
                  <RoadmapView
                    releases={selectedApp.releases}
                    onReleaseClick={handleReleaseClick}
                    onAddRelease={handleAddRelease}
                    isLoading={isFetchingReleases}
                  />
                )}
              </Box>
            ) : !selectedProductId ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  Select a product to view applications
                </Typography>
              </Box>
            ) : filteredApplications.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  No applications in this product yet
                </Typography>
                {/* Hidden: Create Application button
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAppDialogOpen(true)}
                  sx={{ mt: 2 }}
                >
                  Create Application
                </Button>
                */}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  Select an application to view its roadmap
                </Typography>
              </Box>
            )}
          </>
        )}
      </Container>

      <ProductDialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        onSave={handleAddProduct}
      />

      <ApplicationDialog
        open={appDialogOpen}
        onClose={() => setAppDialogOpen(false)}
        onSave={handleAddApplication}
        products={products}
        selectedProductId={selectedProductId}
      />

      <ReleaseDialog
        open={releaseDialogOpen}
        onClose={() => {
          setReleaseDialogOpen(false);
          setEditingRelease(null);
        }}
        onSave={handleSaveRelease}
        initialData={editingRelease || undefined}
        githubRepoUrl={selectedApp?.githubRepoUrl}
      />

      <ReleaseDetailsDialog
        open={releaseDetailsOpen}
        onClose={() => {
          setReleaseDetailsOpen(false);
          setSelectedRelease(null);
        }}
        release={selectedRelease}
        onEdit={handleEditRelease}
        onDelete={handleDeleteRelease}
      />

      <MarketingSummaryDialog
        open={marketingSummaryOpen}
        onClose={() => setMarketingSummaryOpen(false)}
        summary={marketingSummary}
        loading={isSummarizing}
        error={summaryError}
        applicationName={selectedApp?.name || ''}
        releaseVersion={selectedReleaseForSummary?.version || ''}
        releases={selectedApp?.releases || []}
        onVersionChange={handleVersionChangeInDialog}
      />

      <Footer />
    </Box>
  );
}

export default App;
